import React, { useContext, useEffect, useRef, useState } from 'react';
import SearchInput from './SearchInput';
import SrfSearchSelect from './SearchSelect';
import {
  ACTION_UPDATE_SEARCH_PARAMS,
  searchContextStore,
} from '../search/SearchContextProvider';
import {
  getDefaultSearchParams,
  getParamsFromQuerystring,
  getVisibleFilters,
  getTrackingInformation,
} from '../searchHelpers';
import { TRACK_INTERACTION, triggerEvent } from '@fec/assets/js/utils/event';
import classNames from 'classnames';
import { translate } from '../searchHelpers';

export default function SearchForm() {
  const { state: searchContext, dispatch } = useContext(searchContextStore);
  let autocompleteSize = 0;

  const _t = translate.bind(
    null,
    searchContext.searchConfig,
    searchContext.translations,
  );
  let forwardedInputRef = useRef(null);
  // use internal state for form...
  let [searchParams, setSearchParams] = useState({
    q: '',
  });
  let [isFocused, setIsFocused] = useState(false);

  // ...sync with context (and url) on submit
  const handleSubmit = (e) => {
    e.preventDefault();
    blurTextField();
    syncWithContext();
    trackSearch(searchParams); // context.searchParams may not be updated yet, searchParams always are.
  };

  // send tracking event
  const trackSearch = (searchParams) => {
    let info = getTrackingInformation(
      searchContext.searchConfig.portalName,
      searchContext.searchConfig.filters,
      searchParams,
      searchContext.searchConfig.name,
    );

    triggerEvent(TRACK_INTERACTION, {
      event_source: info.source,
      event_name: searchParams.q
        ? searchParams.q.toLowerCase()
        : searchContext.searchParams.q.toLowerCase(),
      event_value:
        info.filters && info.filters !== '0'
          ? info.filters
          : String(autocompleteSize),
      event_type: 'hidden_event',
    });
  };

  // remove focus from input field
  const blurTextField = () => {
    if (forwardedInputRef && forwardedInputRef.current) {
      forwardedInputRef.current.querySelectorAll('input')[0].blur();
    } else {
      // Extreme beauty of mine, un-focus the typeahead
      const elements = document.getElementsByClassName(
        'js-typeahead__input-wrapper',
      );
      if (elements) {
        for (let i = 0; i < elements.length; i++) {
          const inputElements = elements[i].getElementsByTagName('input');
          if (inputElements) {
            for (let j = 0; j < inputElements.length; j++) {
              inputElements[j].blur();
            }
          }
        }
      }
    }
  };

  // combine old and new state
  const handleChange = (params) => {
    let newParams = { [params.key]: params.value };
    let paramValue = params.value;

    // When searching for a range, we have three values, that we need to add to the search request
    if (typeof params.value === 'object' && params.value.type === 'range') {
      newParams = {
        dateFrom: params.value.dateFrom,
        dateTo: params.value.dateTo,
        [params.key]: 'range',
      };
      paramValue = 'range';
    }

    let formState = {
      ...searchParams,
      ...newParams,
    };

    // Clean up dateFrom from potential previous searches
    if (paramValue !== 'range' && formState.dateFrom) {
      delete formState.dateFrom;
    }

    // Clean up dateTo from potential previous searches
    if (paramValue !== 'range' && formState.dateTo) {
      delete formState.dateTo;
    }

    setSearchParams(formState);
    dispatch({ type: ACTION_UPDATE_SEARCH_PARAMS, payload: formState });
    let trackingParams = {};
    trackingParams[params.key] = paramValue; // convert to same format as stored in searchContext
    trackSearch(trackingParams);
  };

  // propagate form state to url context
  const syncWithContext = () => {
    dispatch({ type: ACTION_UPDATE_SEARCH_PARAMS, payload: searchParams });
  };

  // get url params and update state & context
  const initializeFromUrl = () => {
    const urlParams = getParamsFromQuerystring();
    const urlParamsWithDefaults = {
      ...getDefaultSearchParams(true, searchContext.searchConfig.filters),
      ...urlParams,
    };

    // page handling is the only responsibility of loadMore().
    // set it explicitly to 0 and let the rubric listing do the needed steps for paging regarding url handling and context.
    urlParamsWithDefaults['page'] = 0;
    setSearchParams(urlParamsWithDefaults);
  };

  const handleAutocompleteSize = (size) => {
    autocompleteSize = size;
  };

  // on load and popstate-event init from url
  useEffect(() => {
    initializeFromUrl();
    window.addEventListener(
      'popstate',
      (event) => {
        if (event.state) {
          initializeFromUrl();
        }
      },
      false,
    );
  }, []);

  // all special configured portal searches should apply the "portal--search" modifier as well in addition to the specific config name
  const hasFilters =
    getVisibleFilters(searchContext.searchConfig.filters).length > 0;

  return (
    <div
      className={classNames('search-container', {
        'search--active': isFocused,
      })}>
      <form
        onSubmit={handleSubmit}
        className={classNames('search', 'search-form--collection', {
          'search--with-filters': hasFilters && searchParams.q,
        })}
        role="search">
        <SearchInput
          ref={forwardedInputRef}
          searchQuery={searchParams.q}
          onchange={(changedValue) => {
            setSearchParams({ ...searchParams, q: changedValue });
          }}
          trackAutocompleteSize={handleAutocompleteSize}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {searchParams.q && hasFilters && (
          <div className="search-filter-container">
            {getVisibleFilters(searchContext.searchConfig.filters).map(
              (filter) => (
                <SrfSearchSelect
                  key={filter.name}
                  value={searchParams[filter.name]}
                  content={filter}
                  onchange={handleChange}
                  searchParams={searchParams}
                />
              ),
            )}
          </div>
        )}
        <button
          type="submit"
          className="h-offscreen"
          tabIndex="-1"
          aria-hidden="true">
          {_t('searchButtonText')}
        </button>
      </form>
    </div>
  );
}
